<template>
  <div class="full-sidebar">
    <div class="first-section">
      <div class="form-group">
        <BaseText variant="inputLabel">Licence Name</BaseText>
        <input
          type="text"
          name="licenseName"
          id="licenseName"
          class="form-control"
          required
          v-model="licenseName"
        />
      </div>

      <div class="form-group">
        <BaseText variant="inputLabel">Start Date</BaseText>
        <input
          type="text"
          name="startDate"
          class="form-control"
          id="startDate"
          disabled
          :value="formatDate(license.started_at, 'YYYY-MM-DD')"
        />
      </div>

      <div class="form-group">
        <BaseText variant="inputLabel">Expiry Time </BaseText>
        <input
          type="text"
          name="expirtTime"
          class="form-control"
          id="expirtTime"
          :value="license.expired_time"
          disabled
        />
        <!--- in years-->
      </div>

      <div class="form-group">
        <BaseText variant="inputLabel">Description</BaseText>
        <textarea
          name="licenseDescription"
          id="licenseDescription"
          class="form-control"
          required
          placeholder="This license has no description"
          v-model="licenseDescription"
        />
      </div>

      <div class="form-group">
        <div class="title">
          <BaseText variant="inputLabel">Linked Screen</BaseText>
          <!-- <i class="material-icons-outlined align-middle">link_off</i> -->
        </div>

        <div v-if="license.screen_details" class="list-item">
          <i class="material-icons-outlined align-middle">monitor</i>
          <span>{{ license.screen_details.name }}</span>
          <div class="screen-navigation">
            <i class="material-icons-outlined align-middle">link_off</i>
            <i class="material-symbols-outlined align-middle"> arrow_forward </i>
          </div>
        </div>
        <div v-else class="list-item no-screen">
          <i class="material-icons-outlined align-middle no-screen">desktop_access_disabled</i>
          <span>No screen connected</span>
          <div class="screen-navigation"></div>
        </div>
      </div>
    </div>

    <div class="button-container">
      <button
        v-if="isAdmin && license.status !== 'removed'"
        type="button"
        class="button-2-secondary"
        @click="openDeleteLicenseModal"
      >
        <i class="material-icons-outlined align-middle">delete_outline</i>
        Delete License
      </button>

      <button
        type="button"
        class="button-2-primary"
        v-if="isAdmin"
        @click="updateLicense"
        :disabled="isSaving"
      >
        <i v-if="isSaving" class="material-icons align-middle spin-animation">refresh</i>
        <i v-else class="material-icons-outlined align-middle">save</i>
        Save Settings
      </button>

      <DeleteLicenseModal
        @closeModal="showDeleteLicenseModal = false"
        :license="license"
        v-if="showDeleteLicenseModal"
        @deleted="$emit('licenseDeleted')"
      />
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import SidebarContainer from '@/components/common/SidebarContainer.vue';
  import DeleteLicenseModal from '@/components/admin/OrganisationDetails/Licenses/DeleteLicenseModal.vue';
  import { updateLicense } from '@/api/organisation';
  import BaseText from '@ui/atoms/baseText/BaseText.vue';

  export default {
    name: 'EditLicensesSidebar',

    components: { SidebarContainer, DeleteLicenseModal, BaseText },

    props: {
      license: {
        type: Object,
        default: () => null,
      },
    },

    data() {
      return {
        isSaving: false,
        showInfo: false,
        showDeleteLicenseModal: false,
        licenseName: '',
        licenseDescription: '',
      };
    },

    mounted() {
      this.licenseName = this.license?.name;
      this.licenseDescription = this.license?.description;
    },

    watch: {
      license() {
        this.licenseName = this.license?.name;
        this.licenseDescription = this.license?.description;
      },
    },

    computed: {
      isAdmin() {
        return this.$store.getters.isAdministrator;
      },
    },

    methods: {
      async updateLicense() {
        this.isSaving = true;

        try {
          const data = {
            ...this.license,
            name: this.licenseName,
            description: this.licenseDescription,
          };
          await updateLicense(this.license.organisation, this.license.id, data);
          this.$toasted.global.general_success({
            message: 'License successfully updated',
          });
          this.$emit('licenseUpdated');
        } catch (error) {
          this.$toasted.global.general_error({
            message: error?.response?.data?.message || 'Failed to update the license',
          });
        }

        this.isSaving = false;
      },

      openDeleteLicenseModal() {
        this.showDeleteLicenseModal = true;
      },

      goToScreen(screen) {
        this.$router.push({
          name: 'Screen',
          params: {
            screenId: screen.id,
          },
        });
      },

      formatDate(date, format) {
        return moment(date).format(format);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .full-sidebar {
    overflow: hidden;
    position: relative;
    height: 100%;
    background-color: #fff;
    width: 20vw;
    max-width: 20vw;
    min-width: 20vw;
    font-size: 14px;
    padding: 20px;
    margin-left: 8px;
  }

  .first-section {
    display: flex;
    flex-direction: column;
    max-height: 80%;
    overflow: scroll;
  }

  .form-group {
    margin-bottom: 8px;
  }

  .field {
    width: 100%;
    padding: 8px 16px;
    border: 2px solid #e8e8e8;
    border-radius: 8px;
    height: 44px;

    &:hover {
      border: solid 2px $borderGrey2;
    }

    &:disabled {
      border: 2px solid #e8e8e8;
    }
  }

  .field-is-link {
    display: flex;
    justify-content: space-around;
    align-items: center;
    color: $primaryText;
    padding: 8px 8px 8px 12px;
    height: 44px;

    &:hover {
      text-decoration: none;
      border: solid 2px $borderGrey2;
    }
  }
  .disabled {
    pointer-events: none;
    cursor: default;
    color: $secondaryText;
  }
  .screen-grid {
    display: grid;
    grid-template-columns: 1fr 5fr 1fr;
    align-items: center;
    width: 100%;

    :last-child {
      display: flex;
      justify-content: flex-end;
    }
  }

  button {
    width: 100%;
  }
  .button-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 10px;
    background-color: #fff;
    position: sticky;
    bottom: 0px;
  }

  .list-item {
    border-radius: 8px;
    border: solid 2px #e8e8e8;
    padding: 8px;
    display: grid;
    grid-template-columns: 24px auto auto;
    gap: 9px;
    align-items: center;
  }

  .screen-navigation {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: #fff;
    position: absolute;
    bottom: 20px;
    left: 20px;
    right: 20px;
  }
  .no-screen {
    color: $darkgrey;
  }
</style>
